import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { EmptyState } from '@/components/common/empty-state';
import { ShieldOff } from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import type { Permission } from '@/types/api';

export function RequireAuth() {
  const user = useAuthStore((s) => s.user);
  const location = useLocation();

  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return <Outlet />;
}

export function RedirectIfAuthenticated() {
  const user = useAuthStore((s) => s.user);
  return user ? <Navigate to="/dashboard" replace /> : <Outlet />;
}

/**
 * Mirrors the backend's PermissionsGuard so a user never lands on a screen whose
 * every request will 403. SUPER_ADMIN bypasses, exactly as the API does.
 */
export function RequirePermission({ permissions }: { permissions: Permission[] }) {
  const user = useAuthStore((s) => s.user);
  if (!user) return <Navigate to="/login" replace />;

  const allowed =
    user.role === 'SUPER_ADMIN' || permissions.every((p) => user.permissions.includes(p));

  if (allowed) return <Outlet />;

  return (
    <div className="mx-auto flex h-full max-w-lg items-center px-6">
      <EmptyState
        icon={ShieldOff}
        title="You don't have access to this screen"
        description={`It needs the ${permissions.join(', ')} permission. Ask a fleet administrator to grant it.`}
        className="w-full"
      />
    </div>
  );
}
